import { Card, Container, Stack, Input, Textarea, Button } from '@chakra-ui/react';
import WorkspaceLayout from '@/pages/workspace/workspace-layout';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import { toaster } from '@/components/ui/toaster';
import { Field } from '@/components/ui/field';
import useIsCurrentUser from '@/lib/hooks/use-is-current-user';
import useWorkspace from '@/lib/hooks/use-workspace';
import useContentTypesStore from '@/utils/use-content-type';

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

export default function CreateContentLibrary() {
  const navigate = useNavigate();
  const currentUser = useIsCurrentUser();
  const { workspace } = useWorkspace();
  const { workspaceId } = useParams<{
    workspaceId: string;
  }>();
  const { loadContentTypes } = useContentTypesStore();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data: FormData) => {
    if (!workspaceId) {
      return;
    }

    try {
      await api.post(`content-types`, {
        name: data.name,
        description: data.description,
        workspace_id: parseInt(workspaceId),
        sport_type_id: workspace.sport_type_id,
        created_by: currentUser.id,
      });
      const resToken = await api.get(`workspace/${workspaceId}/token`);
      const tokenData = resToken.data.resource;
      if (tokenData) {
        localStorage.setItem('accessToken', tokenData);
      }
      if (workspaceId) {
        loadContentTypes(workspaceId);
      }
      navigate(`/${workspaceId}/content-types`);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to create content library',
      });
    }
  };

  return (
    <WorkspaceLayout>
      <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
        <Card.Header>
          <Container maxW={'3xl'}>
            <Card.Title mb={4} fontSize={'3xl'}>
              Create custom library
            </Card.Title>
          </Container>
        </Card.Header>
        <Card.Body>
          <Container maxW={'3xl'}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack gap={4}>
                <Field label='Name' invalid={!!errors.name} errorText={errors.name?.message}>
                  <Input {...register('name')} />
                </Field>
                <Field label='Description'>
                  <Textarea {...register('description')} rows={4} />
                </Field>
                <Button
                  colorPalette='brand'
                  type={'submit'}
                  width={'full'}
                  mt={5}
                  loading={isSubmitting}
                  loadingText='Loading...'
                >
                  Save
                </Button>
              </Stack>
            </form>
          </Container>
        </Card.Body>
      </Card.Root>
    </WorkspaceLayout>
  );
}
